// Path: ./src/utils/utils.ts

import { GfFlyerItemDto } from "@swagger/typescript-fetch-goflyer";

export function extractEnglishAndChinese(str: string): {
  english: string;
  chinese: string;
} {
  // Include numbers and special characters in the English regular expression
  const englishRegex = /[\w\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]+/;
  // Chinese regular expression remains the same
  const chineseRegex = /[\u4e00-\u9fa5\s]/;

  let englishChars = "";
  let chineseChars = "";

  for (let i = 0; i < str.length; i++) {
    const char: string = str.charAt(i);
    if (englishRegex.test(char)) {
      englishChars += char;
    } else if (chineseRegex.test(char)) {
      chineseChars += char;
    }
  }
  /**
   * A string value that has been processed to:
   * 1. Remove leading/trailing whitespace
   * 2. Replace all tilde (~) characters with single spaces
   * 3. Replace multiple consecutive spaces with a single space
   * @type {string}
   */
  const processedEnglish = englishChars
    .trim()
    .replace(/~/g, " ")
    .replace(/\s\s+/g, " ");

  return {
    english: removeNumbersAfterUnderscore(processedEnglish),
    chinese: removeNumbersAfterUnderscore(chineseChars),
  };
}

export function extractEnglishAndChineseV2(str: string): {
  english: string;
  chinese: string;
} {
  // Include numbers, whitespace, common punctuation, and latin extended characters in the English regular expression
  const englishRegex =
    /[\w\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~\u00A0-\u00FF\u0100-\u017F\u0180-\u024F\u1E00-\u1EFF]/;
  // Chinese regular expression includes Modern & Extended CJK characters and Chinese punctuation
  const chineseRegex =
    /[\u3400-\u4DBF\u4e00-\u9FFF\uF900-\uFAFF\u3000-\u303F\uff00-\uffef]/;

  // Identify spans where Chinese text is interwoven with underscores/digits so we keep them together
  const chineseBridgePattern =
    /[\u3400-\u4DBF\u4e00-\u9FFF\uF900-\uFAFF]+(?:[_\d]+[\u3400-\u4DBF\u4e00-\u9FFF\uF900-\uFAFF]+)+/g;
  const chineseBridgeIndexes = new Set<number>();

  let bridgeMatch: RegExpExecArray | null;
  while ((bridgeMatch = chineseBridgePattern.exec(str)) !== null) {
    const start = bridgeMatch.index ?? 0;
    const matchedValue = bridgeMatch[0];

    for (let i = 0; i < matchedValue.length; i++) {
      chineseBridgeIndexes.add(start + i);
    }
  }

  let englishChars = "";
  let chineseChars = "";

  const punctuationSharedWithChinese = new Set([","]);

  for (let index = 0; index < str.length; index++) {
    const char = str[index];
    const prevChar = index > 0 ? str[index - 1] : "";
    const nextChar = index + 1 < str.length ? str[index + 1] : "";
    const prevPrevChar = index > 1 ? str[index - 2] : "";
    const nextNextChar = index + 2 < str.length ? str[index + 2] : "";

    const prevIsChinese =
      chineseBridgeIndexes.has(index - 1) || chineseRegex.test(prevChar);
    const nextIsChinese =
      chineseBridgeIndexes.has(index + 1) || chineseRegex.test(nextChar);

    const prevChineseLike =
      prevIsChinese ||
      (punctuationSharedWithChinese.has(prevChar) &&
        (chineseBridgeIndexes.has(index - 2) ||
          chineseRegex.test(prevPrevChar)));
    const nextChineseLike =
      nextIsChinese ||
      (punctuationSharedWithChinese.has(nextChar) &&
        (chineseBridgeIndexes.has(index + 2) ||
          chineseRegex.test(nextNextChar)));

    const prevIsEnglish = englishRegex.test(prevChar) && !prevIsChinese;
    const nextIsEnglish = englishRegex.test(nextChar) && !nextIsChinese;

    if (/\s/.test(char)) {
      if (prevChineseLike && nextChineseLike) {
        chineseChars += " ";
      } else {
        englishChars += " ";
      }
      continue;
    }

    if (punctuationSharedWithChinese.has(char)) {
      if (
        (prevChineseLike && !prevIsEnglish) ||
        (nextChineseLike && !nextIsEnglish)
      ) {
        chineseChars += char;
      } else {
        englishChars += char;
      }
      continue;
    }

    if (chineseBridgeIndexes.has(index)) {
      chineseChars += char;
      continue;
    }

    if (englishRegex.test(char)) {
      englishChars += char;
      continue;
    }

    if (chineseRegex.test(char)) {
      chineseChars += char;
      continue;
    }

    // Drop Yen/Yuan currency symbols to align with legacy expectations
    if (char === "¥" || char === "￥") {
      continue;
    }

    // Fallback: treat any other visible character (em dash, emoji, etc.) as English to avoid data loss
    if (char.trim().length === 0) {
      englishChars += " ";
    } else {
      englishChars += char;
    }
  }
  /**
   * A string value that has been processed to:
   * 1. Remove leading/trailing whitespace
   * 2. Replace all tilde (~) characters with single spaces
   * 3. Replace multiple consecutive spaces with a single space
   * @type {string}
   */
  const processedEnglish = englishChars
    .trim()
    .replace(/~/g, " ")
    .replace(/\s\s+/g, " ");

  /**
   * Process Chinese text similarly:
   * 1. Replace tildes with spaces for consistency
   * 2. Trim whitespace
   */
  const processedChinese = chineseChars.replace(/~/g, " ").trim();

  const cleanedEnglish = removeNumbersAfterUnderscoreImproved(processedEnglish)
    .replace(/_+$/g, "") // Remove trailing underscores
    .replace(/__+/g, " ") // Replace multiple underscores with single space
    .replace(/\s+_(?=[a-zA-Z])/g, " ") // Remove space + underscore before letters (artifact)
    .replace(/\s+(?=")/g, "") // Remove spaces before quotes
    .replace(/^["']|["']$/g, "") // Remove leading/trailing quotes
    .replace(/\s{2,}/g, " ") // Collapse multiple spaces
    .trim();

  const cleanedChinese = removeNumbersAfterUnderscoreImproved(processedChinese)
    .replace(/_+$/g, "") // Remove trailing underscores
    .replace(/__+/g, " ") // Replace multiple underscores with single space
    .replace(/\s+_(?=[\u4e00-\u9fa5\u3400-\u4DBF\uF900-\uFAFF])/g, " ") // Remove space + underscore before Chinese
    .replace(/^["']|["']$/g, "") // Remove leading/trailing quotes
    .replace(/\s{2,}/g, " ") // Collapse multiple spaces
    .trim();

  return {
    english: cleanedEnglish,
    chinese: cleanedChinese,
  };
}

interface MyObject {
  field: string;
}

export function ArrangeArrayByCustomOrder(
  objects: MyObject[],
  customOrder: string[]
): MyObject[] {
  const orderMap: { [key: string]: number } = {};

  for (let i = 0; i < customOrder.length; i++) {
    orderMap[customOrder[i]] = i;
  }
  objects.sort((a, b) => {
    const orderA = orderMap[a.field] || Infinity;
    const orderB = orderMap[b.field] || Infinity;
    return orderA - orderB;
  });

  return objects;
}

export function combineAndRemoveDuplicates<T>(
  arr1: T[] = [],
  arr2: T[] = [],
  uniqueProp?: keyof T
): T[] {
  const combinedArray: T[] =
    arr1?.length && arr2?.length
      ? [...arr1, ...arr2]
      : arr1?.length
      ? arr1
      : arr2?.length
      ? arr2
      : [];

  const uniqueMap = new Map<unknown, T>();

  combinedArray.forEach((item) => {
    const key = uniqueProp ? item[uniqueProp] : JSON.stringify(item);
    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, item);
    }
  });

  const uniqueArray = Array.from(uniqueMap.values());

  return uniqueArray;
}

/**
 * Retrieves the start and end dates of the current week.
 * The week starts on Monday and ends on Sunday.
 *
 * @returns {Object} An object containing the start and end dates in ISO string format.
 * @property {string} start - The ISO string representing the start date of the current week.
 * @property {string} end - The ISO string representing the end date of the current week.
 */
export function getCurrentWeekDates(): { start: string; end: string } {
  const now = new Date();
  const currentDay = now.getDay();
  const diff = now.getDate() - currentDay + (currentDay === 0 ? -6 : 1);
  const startOfWeek = new Date(now);
  const endOfWeek = new Date(now);

  startOfWeek.setDate(diff);
  endOfWeek.setDate(diff + 6);

  startOfWeek.setHours(0, 1, 0, 0);
  endOfWeek.setHours(23, 59, 0, 0);

  const formattedStart = startOfWeek.toISOString();
  const formattedEnd = endOfWeek.toISOString();

  return { start: formattedStart, end: formattedEnd };
}

export function removeNumbersAfterUnderscore(input: string): string {
  return input.replace(/_(\d+)/g, "");
}

/**
 * Improved version that only removes numbers after underscore in specific contexts
 * Preserves meaningful identifiers like product codes, brand codes, etc.
 * Only removes patterns that look like auto-generated suffixes
 * Preserves numbers before measurement units (g, ml, lb, kg, oz, etc.)
 */
export function removeNumbersAfterUnderscoreImproved(input: string): string {
  if (!input) {
    return input;
  }

  let result = input;

  // Measurement and quantity units that should preserve their numbers
  const measurementUnits = [
    "g",
    "mg",
    "kg",
    "lb",
    "lbs",
    "oz",
    "ml",
    "l",
    "cl",
    "dl",
    "ft",
    "in",
    "cm",
    "mm",
    "m",
    "gal",
    "qt",
    "pt",
    "cc",
    "floz",
    "pk",
    "pc",
    "pcs",
    "ct",
  ];
  const measurementSet = new Set(measurementUnits);

  // Iteratively remove _digits patterns (handles multiple occurrences)
  // Remove from words with 3+ letters: "product_123" -> "product"
  // But preserve if followed by a unit: "Butter_454g" -> "Butter 454g"
  const measurementPattern = /([a-zA-Z]{3,})_(\d+(?:[.,]\d+)?)([a-zA-Z%]*)/g;

  let previousResult: string;
  do {
    previousResult = result;

    result = result.replace(
      measurementPattern,
      (match, word: string, numeric: string, suffix: string) => {
        const safeSuffix = suffix || "";
        const normalizedSuffix = safeSuffix.toLowerCase();

        if (
          safeSuffix &&
          (measurementSet.has(normalizedSuffix) ||
            measurementUnits.some(
              (unit) => unit.length > 1 && normalizedSuffix.startsWith(unit)
            ))
        ) {
          // Preserve the number with a space: "Butter_454g" -> "Butter 454g"
          return `${word} ${numeric}${safeSuffix}`;
        }

        if (!safeSuffix) {
          // Remove _digits when there is no meaningful suffix
          return word;
        }

        // Otherwise, remove the _digits part but keep any textual suffix
        return word + safeSuffix;
      }
    );
  } while (result !== previousResult);

  // Remove standalone _123 patterns after spaces (single underscores only)
  result = result.replace(/\s+_(\d+)\b/g, " ");

  // Remove _123 at the beginning
  result = result.replace(/^_(\d+)\s+/g, "");

  return result.trim();
}

export function formatDateToDayMonthYear(input: string | Date): string {
  let date: Date;

  if (typeof input === "string") {
    date = new Date(input);
  } else {
    date = input;
  }

  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();

  const formattedDate = `${day}-${month}-${year}`;

  return formattedDate;
}

/**
 * Extracts the place name and coordinates from a given Google Maps URL.
 *
 * @param {string} url - The Google Maps URL from which to extract the place name and coordinates.
 * @returns {object} An object containing the extracted place name, latitude, and longitude.
 *
 * @property {string | null} place - The extracted place name from the URL, or null if not found.
 * @property {number | undefined} latitude - The extracted latitude coordinate from the URL, or undefined if not found.
 * @property {number | undefined} longitude - The extracted longitude coordinate from the URL, or undefined if not found.
 */
export function extractPlaceAndCoordinates(url: string): {
  place: string | null;
  latitude: number | undefined;
  longitude: number | undefined;
} {
  const placeRegex = /\/place\/([^/@]*)/;
  const coordRegex = /@([0-9.-]*),([0-9.-]*)/;

  const placeMatch = url.match(placeRegex);
  const coordMatch = url.match(coordRegex);

  return {
    place: placeMatch
      ? decodeURIComponent(placeMatch[1].replace(/\+/g, " "))
      : null,
    latitude: coordMatch ? parseFloat(coordMatch[1]) : undefined,
    longitude: coordMatch ? parseFloat(coordMatch[2]) : undefined,
  };
}

export const convertBase64ToBlob = (base64: string, contentType = ""): Blob => {
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

/**
 * Converts a given number of bytes into a human-readable format, choosing the appropriate unit.
 * If the integer part of the result is greater than 0, it omits decimal precision.
 *
 * @param {number} bytes - The number of bytes to be formatted.
 * @returns {string} The formatted size in the appropriate unit (e.g., "Bytes", "KB", "MB").
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);

  return (
    (value >= 1 ? Math.floor(value) : parseFloat(value.toFixed(2))) +
    " " +
    sizes[i]
  );
}

export function formatRegularPriceString(input: string): string {
  // Step 1: Check for invalid characters (anything other than digits, '.', and '$')
  if (/[^0-9$.]/.test(input)) {
    return input;
  }

  // Step 2: Check if input is purely numeric (digits with optional decimal point)
  if (/^\d+(\.\d+)?$/.test(input)) {
    // Input is numeric and needs a '$' added
    /**
     * I remove the $ in front for now since it is currently confusing designer. but I keep the logic here just in case in the future, they might need this auto adding $
     */
    return input;
  }

  // Step 3: Check if input is a valid currency string with '$' sign
  if (/^\$\d+(\.\d+)?$/.test(input)) {
    // Input is a valid currency string
    return input;
  }

  return input;
}

/**
 * Removes double quotes from the start and end of a string.
 *
 * @param input - The string to process
 * @returns The string without surrounding double quotes
 */
export function removeDoubleQuotes(input: string): string {
  return input.replace(/^"|"$/g, "");
}

export const instanceOfFlyerItem = function (
  data: any
): data is GfFlyerItemDto {
  return "flyerItemList" in data;
};
